/**
 * Training — unit production queues on buildings with a `trains` list.
 * Cost is paid upfront; when the data `build_time` elapses the caller spawns
 * the formation via the onComplete callback. Tech gating (`requires_techs`)
 * is not enforced yet — Phase 4 (technologies/echelons).
 */
import type { GameDB } from "../data/gamedb";
import type { UnitDef } from "../data/types";
import type { PlacedBuilding } from "./buildings";
import type { ResearchState } from "./research";

export interface TrainingOrder {
  unitId: string;
  /** Organization tier mustered by this order (sim/organization.ts). */
  tierId: string;
  remaining: number;
  total: number;
}

export interface CompletedTraining {
  buildingUid: number;
  unitId: string;
  /** Organization tier locked at enqueue (sim/organization.ts). */
  tierId: string;
}

export class TrainingQueues {
  private queues = new Map<number, TrainingOrder[]>();

  /**
   * Enqueue a unit. timeScale stretches the data build_time for larger
   * musters (a legion takes proportionally longer to raise than a
   * warband); the tier is locked at enqueue — what you pay for is what
   * musters.
   */
  enqueue(
    buildingUid: number,
    unit: UnitDef,
    tierId = "",
    timeScale = 1,
  ): void {
    const buildTime = ((unit.extra["build_time"] as number) ?? 20) * timeScale;
    const q = this.queues.get(buildingUid) ?? [];
    q.push({ unitId: unit.id, tierId, remaining: buildTime, total: buildTime });
    this.queues.set(buildingUid, q);
  }

  /** Restore a partially-completed order (save/load). */
  restoreOrder(
    buildingUid: number,
    unitId: string,
    remaining: number,
    total: number,
    tierId = "",
  ): void {
    const q = this.queues.get(buildingUid) ?? [];
    q.push({ unitId, tierId, remaining, total });
    this.queues.set(buildingUid, q);
  }

  /** All queued orders, for save/load. */
  allOrders(): Array<{ buildingUid: number; unitId: string; tierId: string; remaining: number; total: number }> {
    const out: Array<{ buildingUid: number; unitId: string; tierId: string; remaining: number; total: number }> = [];
    for (const [buildingUid, q] of this.queues) {
      for (const o of q) out.push({ buildingUid, ...o });
    }
    return out;
  }

  clear(): void {
    this.queues.clear();
  }

  /** Advance all queues; returns completions for the caller to spawn. */
  tick(dt: number): CompletedTraining[] {    const done: CompletedTraining[] = [];
    for (const [uid, q] of this.queues) {
      if (q.length === 0) continue;
      q[0].remaining -= dt;
      if (q[0].remaining <= 0) {
        const order = q.shift()!;
        done.push({ buildingUid: uid, unitId: order.unitId, tierId: order.tierId });
      }
    }
    return done;
  }

  queueOf(buildingUid: number): TrainingOrder[] {
    return this.queues.get(buildingUid) ?? [];
  }

  headProgress(buildingUid: number): number {
    const q = this.queues.get(buildingUid);
    if (!q || q.length === 0) return 0;
    return 1 - q[0].remaining / q[0].total;
  }
}

/**
 * Units a building can train: implemented, at or below the current echelon,
 * with all `requires_techs` researched. Tech gating deferred from Phase 3 —
 * enforced here.
 */
export function trainableUnits(
  building: PlacedBuilding,
  db: GameDB,
  echelonIdx = 1,
  research?: ResearchState,
): UnitDef[] {
  const ids = (building.def.extra["trains"] as string[]) ?? [];
  const out: UnitDef[] = [];
  for (const id of ids) {
    const u = db.units.get(id);
    if (!u || u.extra["implemented"] === false) continue;
    if (u.echelon > echelonIdx) continue;
    const reqTechs = (u.extra["requires_techs"] as string[]) ?? [];
    if (research && reqTechs.some((t) => !research.completed.has(t))) continue;
    out.push(u);
  }
  return out;
}

export function unitCost(unit: UnitDef): Record<string, number> {
  return (unit.extra["cost"] as Record<string, number>) ?? {};
}
